/**
 * Auth hook for Admin Panel
 *
 * REUTILIZA el mismo flujo Google OAuth del backend (auth-google → auth-google-callback).
 * Tras autenticar, el acceso admin lo decide el backend (`isAdmin` vía profiles.is_admin).
 *
 * SRP: Este hook solo gestiona estado de auth + admin check.
 * La lógica HTTP vive en api-client.ts.
 */

import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiClient, type StoredSession } from '@/services/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdminUser {
    id: string;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
}

interface AuthState {
    user: AdminUser | null;
    isLoading: boolean;
    isAdmin: boolean;
    error: string | null;
}

interface AuthContextType extends AuthState {
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Map raw backend user data to our AdminUser type (auth-me uses name/picture, OAuth callback uses display_name/avatar_url) */
function toAdminUser(raw: any): AdminUser {
    return {
        id: raw.id,
        email: raw.email || '',
        displayName: raw.display_name || raw.displayName || raw.name || raw.email || null,
        avatarUrl: raw.avatar_url || raw.avatarUrl || raw.picture || null,
    };
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        isLoading: true,
        isAdmin: false,
        error: null,
    });

    // 1. On mount: check existing session
    useEffect(() => {
        restoreSession();
    }, []);

    // 2. Listen for forced logout (e.g. 401 response)
    useEffect(() => {
        const handler = () => setState({ user: null, isLoading: false, isAdmin: false, error: null });
        window.addEventListener('auth:session-cleared', handler);
        return () => window.removeEventListener('auth:session-cleared', handler);
    }, []);

    // 3. Handle OAuth callback (?code=xxx in URL)
    useEffect(() => {
        const code = new URLSearchParams(window.location.search).get('code');
        if (code) {
            window.history.replaceState({}, '', window.location.pathname);
            handleOAuthCallback(code);
        }
    }, []);

    /** Restore session from localStorage + validate with backend */
    async function restoreSession() {
        const session = apiClient.getSession();
        if (!session?.access_token) {
            setState({ user: null, isLoading: false, isAdmin: false, error: null });
            return;
        }

        try {
            // Validate token with backend (auth-me returns user object directly, not { user: ... })
            // api-client aplica timeout; si falla → login, nunca “Verificando acceso…” eterno.
            const { data, error } = await apiClient.get<any>('/auth-me');
            if (error || !data || !data?.id) {
                apiClient.clearSession();
                setState({
                    user: null,
                    isLoading: false,
                    isAdmin: false,
                    error: error?.code === 'TIMEOUT'
                        ? 'No se pudo verificar la sesión. Vuelve a iniciar sesión.'
                        : null,
                });
                return;
            }

            applyUser(data);
        } catch {
            apiClient.clearSession();
            setState({
                user: null,
                isLoading: false,
                isAdmin: false,
                error: 'No se pudo verificar la sesión. Vuelve a iniciar sesión.',
            });
        }
    }

    /** Exchange OAuth code for session (same endpoint as main app) */
    async function handleOAuthCallback(code: string) {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        const redirectUri = window.location.origin;
        const { data, error } = await apiClient.post<{
            access_token?: string;
            accessToken?: string;
            refresh_token?: string;
            refreshToken?: string;
            expires_in?: number;
            expiresIn?: number;
            user: any;
        }>('/auth-google-callback', { code, redirectUri }, { requiresAuth: false });

        if (error || !data?.user) {
            setState(prev => ({ ...prev, isLoading: false, error: error?.message || 'Error de autenticación' }));
            return;
        }

        // Save session (handle both camelCase and snake_case from backend)
        const expiresIn = data.expires_in ?? data.expiresIn ?? 3600;
        apiClient.saveSession({
            access_token: data.access_token || data.accessToken || '',
            refresh_token: data.refresh_token || data.refreshToken,
            expires_at: Date.now() + expiresIn * 1000,
            user: data.user,
        });

        applyUser(data.user);
    }

    /** Set user state with admin check (fuente de verdad: backend / profiles.is_admin) */
    function applyUser(rawUser: any) {
        const user = toAdminUser(rawUser);
        const admin = rawUser?.isAdmin === true;

        setState({
            user,
            isLoading: false,
            isAdmin: admin,
            error: admin ? null : 'Tu cuenta no tiene permisos de administrador',
        });
    }

    // ─── Actions ────────────────────────────────────────────────────────────

    /**
     * Redirect to Google OAuth.
     * Reuses the SAME auth-google Azure Function as the main app.
     * After Google auth, the backend redirects back here with ?code=xxx.
     */
    const login = useCallback(() => {
        const redirectUri = window.location.origin;
        // apiClient.baseUrl may be relative ("/api") in dev, so build absolute URL for browser redirect
        const base = apiClient.baseUrl.startsWith('http')
            ? apiClient.baseUrl
            : `${window.location.origin}${apiClient.baseUrl}`;
        const authUrl = `${base}/auth-google?redirect=${encodeURIComponent(redirectUri)}`;
        window.location.href = authUrl;
    }, []);

    const logout = useCallback(() => {
        apiClient.clearSession();
        setState({ user: null, isLoading: false, isAdmin: false, error: null });
    }, []);

    return (
        <AuthContext.Provider value={{ ...state, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextType {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
}
